import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  useLocalStorage,
  useMemoWithDeepCompare,
} from '@fortress-technology-solutions/fortress-component-library/hooks';
import useIntl from '../../hooks/useIntl';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import {
  columns,
  FIELD_MAP,
  PROPERTY_PATH_MAP,
  proposedChangeTypes,
} from './constants';
import useRows from './hooks.useRows';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { getAggregatedProgramTypes } from '../../utils/affordable';
import useCSVExport from './hooks.useCSVExport';
import { PROPERTY_PATH_MAP as prevPayloadRef } from '../ManageDelinquency/constantsv2';
import useHeaders from './hooks.useHeaders';
import useFooterRow from './hooks.useFooterRow';
import useRentForecastQuery from './hooks.useRentForecastQuery';
import useRentForecastUpdateMutation from './hooks.useRentForecastUpdateMutation';
import useRentForecastMutation from './hooks.useRentForecastMutation';
import useRentValuesDirectUpdateMutation from './hooks.useRentValuesDirectUpdateMutation';
import {
  getFormattedPercentage,
  getNewFilteredColumns,
  mapAllowances,
} from './utils';
import _ from 'lodash';
import usePermissionsHook from 'components/ElementWithPermissions/usePermissionsHook';

const NAME = 'rent-forecast';

export const useManageRentForecast = ({ isRD }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const {
    organizationId,
    id: propertyId,
    propertyClass: { name: propertyClassName },
  } = useSelectedProperty();
  const isAffordableMixedProperty = ['Mixed', 'Affordable'].includes(
    propertyClassName,
  );
  const isAffordableProperty = ['Affordable'].includes(propertyClassName);

  // get the id from previous iteration, (backwards compatible)
  const { localStorageState: backwardsCompatibleCurrentRentForecastId } =
    useLocalStorage(`${NAME}_rentForecastId`, 'string', '');

  const {
    localStorageState: currentRentForecastId,
    setLocalStorage: setCurrentRentForecastId,
  } = useLocalStorage(
    'current-rent-forecast-id',
    'string',
    backwardsCompatibleCurrentRentForecastId ?? '',
  );
  const hasEditPermission = usePermissionsHook({
    scope: ['forecast-rents-edit'],
  });
  // should have a unique tableName for each rent forecast
  const tableName = useUniqueTableName(`${NAME}_${currentRentForecastId}`);

  const [forecastDataResponse, setForecastDataResponse] = useState(null);
  const [results, setResults] = useState([]);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [rentForecastValues, setRentForecastValues] = useState({
    name: '',
    date: null,
  });
  const [unitInfo, setUnitInfo] = useState({
    totalUnits: null,
    occupiedUnits: null,
  });
  const [currentRentForecast, _setCurrentRentForecast] = useState(null);
  const [selectedProposedChangeType, setSelectedProposedChangeType] =
    useState('dollar');

  const canEdit = hasEditPermission && !currentRentForecast?.approvedFrom;

  const selectedForecastIdRef = useRef(currentRentForecastId);

  const [seedInitialProposedState, setSeedInitialProposedState] =
    useState(null);
  const loadingFloorPlans = useRef(false);

  const handleResults = useCallback(
    (data) => {
      const rentForecastIdToUse =
        selectedForecastIdRef.current &&
        data?.rentForecasts?.some(
          (rf) => rf.id === selectedForecastIdRef.current,
        )
          ? selectedForecastIdRef.current
          : currentRentForecastId &&
            data?.rentForecasts?.some((rf) => rf.id === currentRentForecastId)
          ? currentRentForecastId
          : data?.rentForecasts?.[0]?.id;

      const rentForecast = data?.rentForecasts?.find(
        (item) => item?.id === rentForecastIdToUse,
      );

      if (!rentForecast) return;

      setForecastDataResponse(data);
      setCurrentRentForecast(rentForecast);
    },
    [currentRentForecastId],
  );

  const setFloorPlanData = useCallback(
    (data, rentForecast) => {
      const { isHUDFloorPlan, isLIHTCFloorPlan, isRDFloorPlan } =
        getAggregatedProgramTypes(data?.floorPlans);

      const newFilteredColumns = getNewFilteredColumns({
        isRDFloorPlan,
        isHUDFloorPlan,
        isLIHTCFloorPlan,
        isAffordableMixedProperty,
      });

      const newData = data?.floorPlans?.map((item) => {
        const floorPlanRelation =
          item?.fpr?.find(
            (rel) =>
              rel.rentForecastId === rentForecast.approvedFrom ||
              rel.rentForecastId === rentForecast.createdFrom ||
              rel.rentForecastId === rentForecast.id,
          ) ?? {};

        const allowances = mapAllowances({
          allowancesArray: item?.allowances || [],
          allowanceValues: item?.fpua || [],
          intl,
        });

        if (data?.totalUnits || data?.occupiedUnits) {
          setUnitInfo({
            totalUnits: data.totalUnits,
            occupiedUnits: data.occupiedUnits,
          });
        }

        return {
          floorPlan: item.internalName,
          floorPlanLink: `/property/${propertyId}/floorPlan/${item.id}`,
          bedsBaths: `${item.nBeds || 0} / ${item.nBaths || 0}`,
          isRDFloorPlan,
          numUnits: item?.floorplanUnitCount?.numTotalUnits || 0,
          unitType: isRDFloorPlan ? item?.unitTypeDetails?.name : null,
          id: item.id,
          allowances,
          newnoteRentProposedChangeValue:
            floorPlanRelation.newNoteRentProposedChangeValue ?? 0,
          newbasicRentProposedChangeValue:
            floorPlanRelation.newBasicRentProposedChangeValue ?? 0,
          newHUDGrossRentProposedChangeValue:
            floorPlanRelation.newHUDGrossRentProposedChangeValue ?? 0,
          newnonOptionalChargeProposedChangeValue:
            floorPlanRelation.newNonOptionalChargeProposedChangeValue ?? 0,
          newhud236BasicRentProposedChangeValue:
            floorPlanRelation.newHud236BasicRentProposedChangeValue ?? 0,
          newhud236MarketRentProposedChangeValue:
            floorPlanRelation.newHud236MarketRentProposedChangeValue ?? 0,
          newTHDAUtilityAllowanceProposedChangeValue:
            floorPlanRelation.newTHDAUtilityAllowanceProposedChangeValue ?? 0,
          newnonOptionalCharge: floorPlanRelation.newNonOptionalCharge ?? 0,
          newhud236BasicRent: floorPlanRelation.newHud236BasicRent ?? 0,
          newhud236MarketRent: floorPlanRelation.newHud236MarketRent ?? 0,
          newTHDAUtilityAllowance:
            floorPlanRelation.newTHDAUtilityAllowance ?? 0,
          newRDUtilityAllowanceProposedChangeValue:
            floorPlanRelation.newRDUtilityAllowanceProposedChangeValue ?? 0,
          newfinancialMarketRentAmount:
            floorPlanRelation.newFinancialMarketRentAmount ?? 0,
          newfinancialMarketRentAmountProposedChangeValue:
            floorPlanRelation.newFinancialMarketRentAmountProposedChangeValue,
          newbaseMarketRentAmountProposedChangeValue:
            floorPlanRelation.newBaseMarketRentAmountProposedChangeValue ?? 0,
          newestimatedDepositAmountProposedChangeValue:
            floorPlanRelation.newEstimatedDepositAmountProposedChangeValue ?? 0,
          newquotingRentAmountProposedChangeValue:
            floorPlanRelation.newQuotingRentAmountProposedChangeValue ?? 0,
          newHudGrossRentProposedChange:
            floorPlanRelation.newHudGrossRentProposedChange ?? 0,
          newQuotingRentAmountPotentialIncome:
            floorPlanRelation.newQuotingRentAmountPotentialIncome ?? 0,
          newEstimatedDepositAmountProposedChange:
            floorPlanRelation.newEstimatedDepositAmountProposedChange ?? 0,
          newUtilityAllowanceProposedChange:
            floorPlanRelation.newUtilityAllowanceProposedChange ?? 0,
          ...allowances.reduce((acc, allowance) => {
            return {
              ...acc,
              [`current${allowance.uaMessage}`]: allowance.currentRate,
              [`total${allowance.uaMessage}Income`]:
                allowance.currentRate *
                  item?.floorplanUnitCount?.numTotalUnits || null,
            };
          }, {}),
          ...newFilteredColumns.reduce((acc, column) => {
            return {
              ...acc,
              [`current${column}`]: item?.[column],
              [`total${column}Income`]:
                item?.[column] * item?.floorplanUnitCount?.numTotalUnits ||
                null,
            };
          }, {}),
        };
      });

      if (data?.totalUnits || data?.occupiedUnits) {
        setUnitInfo({
          totalUnits: data.totalUnits,
          occupiedUnits: data.occupiedUnits,
        });
      }

      if (newData?.length) {
        setResults(newData);
        setSeedInitialProposedState(
          buildInitialProposedState(data?.floorPlans ?? [], rentForecast.id),
        );
      }

      setFilteredColumns(newFilteredColumns);
    },
    [propertyId, setFilteredColumns, setResults, setUnitInfo],
  );

  const {
    data: rentForecasts,
    isFetching: isQueryLoading,
    refetch: reloadRentForecastList,
  } = useRentForecastQuery(organizationId, propertyId, handleResults);

  const buildInitialProposedState = useCallback(
    (floorPlans, rentForecastId) => {
      return floorPlans.reduce((seed, fp) => {
        const rel = fp.fpr?.find((r) => r.rentForecastId === rentForecastId);
        if (!rel) return seed;

        FIELD_MAP.forEach(([from, suffix]) => {
          const v = rel[from];
          if (v != null) seed[`${fp.id}-new${suffix}ProposedChangeValue`] = v;
        });
        return seed;
      }, {});
    },
    [],
  );

  // Track the last forecast ID that was set to prevent duplicate calls
  const lastSetForecastIdRef = useRef(null);

  const setCurrentRentForecast = useCallback(
    (rentForecast) => {
      if (!rentForecast) return;
      // Skip if this is the same forecast we just set
      if (rentForecast?.id === lastSetForecastIdRef.current) return;

      _setCurrentRentForecast(rentForecast);
      setCurrentRentForecastId(rentForecast?.id);
      selectedForecastIdRef.current = rentForecast?.id;
      lastSetForecastIdRef.current = rentForecast?.id;
      loadingFloorPlans.current = true;

      if (rentForecast?.proposedChangeType) {
        setSelectedProposedChangeType(rentForecast.proposedChangeType);
      }
    },
    [setCurrentRentForecastId],
  );

  const { mutate: createRentForecast, isLoading } = useRentForecastMutation(
    organizationId,
    propertyId,
    setCurrentRentForecast,
    isRD,
  );

  const { mutate: updateRentForecast } = useRentForecastUpdateMutation(
    organizationId,
    propertyId,
    currentRentForecast?.id,
  );

  const { allowances, isRDFloorPlan } = useMemo(() => {
    return {
      allowances: (results ?? [])
        .flatMap((item) => item.allowances ?? [])
        .filter(
          (allowance, index, self) =>
            index === self.findIndex((a) => a.id === allowance.id),
        ),
      isRDFloorPlan: results?.some((item) => item.isRDFloorPlan),
    };
  }, [results]);

  const headers = useHeaders({
    intl,
    filteredColumns,
    allowances,
    isRDFloorPlan,
    isAffordableProperty,
    currentRentForecastId: currentRentForecast?.id,
    isApproved: Boolean(currentRentForecast?.approvedFrom),
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name: tableName,
    headers,
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const { rows, proposedState, resetProposedState, setProposedState } = useRows(
    {
      sortedAndFilteredResults,
      proposedChangeType: proposedChangeTypes[selectedProposedChangeType],
      forecastId: currentRentForecast?.id,
      seedInitialProposedState,
      syncKey: currentRentForecast?.id,
      disableInputs: !canEdit,
    },
  );

  const { mutate: updateRentValuesDirect, isLoading: isUpdatingDirect } =
    useRentValuesDirectUpdateMutation(
      organizationId,
      propertyId,
      currentRentForecast?.id,
      selectedProposedChangeType,
      !!currentRentForecast?.snapshotData,
    );

  const submitDirectRentUpdate = (customPayload = null) => {
    if (!currentRentForecast?.id) return;

    const isPercent = selectedProposedChangeType === 'percent';

    const mapRowToPayload = (row) =>
      FIELD_MAP.reduce(
        (obj, [, suffix, def]) => {
          let val = row?.[`new${suffix}ProposedChangeValue`]?.value ?? def;
          if (!isPercent && val === 1) val = 0;
          return obj;
        },
        { floorPlanId: row.id },
      );

    const payload =
      customPayload ?? rows.filter((r) => !!r.id).map(mapRowToPayload);

    // Update the cached rent forecast data if the response is for updating the rent forecast object
    updateRentValuesDirect(payload, {
      onSuccess: ({ response }) => {
        if (response?.id) {
          queryClient.setQueryData(
            ['useRentForecastQuery', organizationId, propertyId],
            (old) => {
              if (!old?.rentForecasts) return old;
              return {
                ...old,
                rentForecasts: old.rentForecasts.map((r) =>
                  r.id === response.id ? response : r,
                ),
              };
            },
          );
        }
      },
    });
  };

  const [previousRentSpread, setPreviousRentSpread] = useState('initial');

  const previousChangesRef = useRef(
    Object.fromEntries(FIELD_MAP.map(([, s]) => [s, 'initial'])),
  );

  useEffect(() => {
    if (!currentRentForecast) {
      return;
    }

    if (currentRentForecast.approvedFrom) {
      // Load floor plan values from approved snapshot
      setFloorPlanData(
        currentRentForecast.approvedSnapshotData,
        currentRentForecast,
      );
    } else if (currentRentForecast.createdFrom) {
      setFloorPlanData(currentRentForecast.snapshotData, currentRentForecast);
    } else {
      setFloorPlanData(forecastDataResponse, currentRentForecast);
    }
  }, [currentRentForecast]);

  useEffect(() => {
    if (currentRentForecast && previousRentSpread === 'initial') {
      setPreviousRentSpread(currentRentForecast.rentSpread);
      previousChangesRef.current = Object.fromEntries(
        FIELD_MAP.map(([, suffix]) => {
          const prop = `new${_.capitalize(suffix)}ProposedChangeValue`;
          return [suffix, currentRentForecast[prop]];
        }),
      );
    }
  }, [currentRentForecast]);

  useEffect(() => {
    if (!currentRentForecast) return;

    const changed = FIELD_MAP.some(([, suffix]) => {
      const key = `new${suffix.charAt(0).toUpperCase()}${suffix.slice(
        1,
      )}ProposedChangeValue`;
      return previousChangesRef.current[suffix] !== currentRentForecast[key];
    });

    if (!changed) return;

    previousChangesRef.current = Object.fromEntries(
      FIELD_MAP.map(([, suffix]) => {
        const key = `new${suffix.charAt(0).toUpperCase()}${suffix.slice(
          1,
        )}ProposedChangeValue`;
        return [suffix, currentRentForecast[key]];
      }),
    );
  }, [currentRentForecast, rows, setProposedState]);

  useEffect(() => {
    prevPayloadRef.current = '';
  }, [currentRentForecast?.id]);

  useEffect(() => {
    if (
      !currentRentForecast ||
      previousRentSpread === 'initial' ||
      currentRentForecast.rentSpread === previousRentSpread
    )
      return;

    const column = 'noteRent';
    const fieldName = `new${column}ProposedChange`;
    const newState = rows.reduce((acc, row) => {
      const stateKey = `${row.id}-${fieldName}`;
      acc[stateKey] = currentRentForecast.rentSpread;
      return acc;
    }, {});
    setProposedState(newState);
    setPreviousRentSpread(currentRentForecast.rentSpread);
  }, [currentRentForecast]);

  const footerRow = useFooterRow({ rows });

  const { initialSelectedColumns, initialColumnsOrder } =
    useMemoWithDeepCompare(
      {
        initialSelectedColumns: currentRentForecast?.showHideState,
        initialColumnsOrder: currentRentForecast?.columnOrder,
      },
      [currentRentForecast?.showHideState, currentRentForecast?.columnOrder],
    );

  const onSettingsClose = useCallback(({ selectedColumns, columnOrder }) => {
    updateRentForecast({
      id: currentRentForecast?.id,
      showHideState: selectedColumns,
      columnOrder,
    });
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    handleSettingsClose,
  } = useTableManageColumns({
    name: tableName,
    headers,
    firstRow: rows[0],
    initialSelectedColumns,
    initialColumnsOrder,
    forceOverwrite: true, // to overwrite localStorage when the initial column values change
    onSettingsClose,
  });

  const proposedValueType = proposedChangeTypes[selectedProposedChangeType];
  const usePercentage = proposedValueType === '%';

  const csvRows = useMemo(
    () =>
      rows.map((row) => {
        const newRow = { ...row };
        Object.keys({
          ...columns,
          'RD Utility Allowance': 'RD Utility Allowance',
        }).forEach((column) => {
          const rowValue = row[`new${column}ProposedChangeValue`]?.value;
          newRow[`new${column}ProposedChange`] = {
            ...row[`new${column}ProposedChange`],
            value: getFormattedPercentage(rowValue, usePercentage),
          };
        });
        newRow.bedsBaths.value = newRow?.bedsBaths?.value.replace(/ /g, '');
        return newRow;
      }),
    [rows, usePercentage],
  );

  const { onCSVButtonClick } = useCSVExport({
    filteredHeaders,
    rows: [...csvRows, footerRow],
    hasAnyFilters: Boolean(Object.keys(filterState)?.length),
    selectedRentForecastName: currentRentForecast?.name,
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows.length ?? 0,
    createRentForecast,
    currentRentForecast,
    dateState,
    filterState,
    filterTypeState,
    footerRow,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    handleSettingsClose,
    headers: filteredHeaders,
    isLoading,
    isQueryLoading,
    onCSVButtonClick,
    order,
    orderBy,
    rentForecasts,
    rentForecastValues,
    rows,
    searchState,
    selectedColumns,
    selectedProposedChangeType,
    setCurrentRentForecast,
    setRentForecastValues,
    setSelectedProposedChangeType,
    tableName,
    totalCount: results.length ?? 0,
    unitInfo,
    updateRentForecast,
    submitDirectRentUpdate,
    resetProposedState,
    proposedState,
    isUpdatingDirect,
    setProposedState,
    reloadRentForecastList,
    loadingFloorPlans,
    setCurrentRentForecastId,
    canEdit,
    hasEditPermission,
  };
};
